import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useCustom, useTranslate } from "@refinedev/core";
import {
  SearchIcon,
  SparklesIcon,
  ClockIcon,
  GridIcon,
  ArrowRightIcon,
  PawIcon,
  UserIcon,
  InvoiceIcon,
} from "@fawkes/ui";

import {
  DEBOUNCE_MS,
  DROPDOWN_LIMIT,
  MIN_CHARS,
  SearchHitCard,
  groupTypeIcon,
  isQuestionLike,
  orderedGroups,
  visibleTotal,
  type DropdownTab,
  type SearchHitDto,
  type SearchHitType,
  type SearchResponseDto,
} from "./searchShared";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";

// Search-migration E3 (FR14/FR15) — the real global-search header dropdown, replacing the disabled
// `fawkes-shell-search` placeholder. A near-1:1 port of the Jmix `GlobalSearch.java`: 250ms debounce,
// 3-char floor, grouped result cards, type tabs, recent searches (localStorage), the always-present
// "Slim zoeken" suffix pill, the in-dropdown AI block for question-like input, footer + keyboard +
// click-outside, and the reparent-to-<body> fixed-position dropdown (here a React portal — the idiom
// for Jmix's `positionDropdown` body-reparent, which escapes the header's stacking context).
//
// Declared divergences from GlobalSearch.java (parity = match, with divergences flagged, per the epic):
//  • The current Jmix `onInputChanged` HIDES the dropdown for question-like input and leaves
//    `renderAiBlockIfQuestionLike` unwired (dead). FR14 + the E3 stories explicitly require the
//    in-dropdown AI block, so E3 revives that method's design — a question (≥3 chars) opens the
//    dropdown with the AI block (hint + "Toepassen" → ?ai=1&q=) instead of literal results.
//  • Recent searches are remembered on an EXPLICIT navigation (Enter / footer / AI "Toepassen"), not on
//    every debounced literal keystroke as Jmix's `onInputChanged` does (`rememberRecentSearch(value)`).
//    Remembering each partial term would pollute the 5-slot history with "be"/"bee"/"beer"/… ; recording
//    only the searches the operator actually commits to is the deliberate, cleaner behaviour here.

const RECENT_KEY = "fawkes.search.recent";
const RECENT_MAX = 5;

function readRecent(): string[] {
  try {
    const raw = globalThis.localStorage?.getItem(RECENT_KEY);
    const list: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    return list.filter((x): x is string => typeof x === "string").slice(0, RECENT_MAX);
  } catch {
    return [];
  }
}

/** Recent searches over localStorage `fawkes.search.recent` — max 5, dedup, most-recent-first (Jmix). */
function useRecentSearches() {
  const [recent, setRecent] = useState<string[]>(() => readRecent());
  const remember = useCallback((query: string) => {
    const q = query.trim();
    if (!q) return;
    setRecent((prev) => {
      const next = [q, ...prev.filter((x) => x !== q)].slice(0, RECENT_MAX);
      try {
        globalThis.localStorage?.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        /* ignore quota / disabled storage */
      }
      return next;
    });
  }, []);
  return { recent, remember };
}

const TAB_DEFS: { id: DropdownTab; labelKey: string; icon: ReactNode }[] = [
  { id: "all", labelKey: "search.tab.all", icon: <GridIcon width={15} height={15} /> },
  { id: "animal", labelKey: "search.tab.animals", icon: <PawIcon width={15} height={15} /> },
  { id: "relation", labelKey: "search.tab.relations", icon: <UserIcon width={15} height={15} /> },
  { id: "invoice", labelKey: "search.tab.invoices", icon: <InvoiceIcon width={15} height={15} /> },
];

export interface GlobalSearchProps {
  /**
   * The current results-page query — set by the shell after navigating to `/zoekresultaten`. When it
   * changes the bar is filled and the dropdown closed (port of Jmix `syncQueryFromUrl`). Undefined off
   * the results page (the bar is then left as-is).
   */
  syncQuery?: string;
  /** Enter / footer / AI "Toepassen": navigate to the full results page (plain or `ai=1`). */
  onNavigateResults: (query: string, ai: boolean) => void;
  /** A result-card click: open the entity detail via the shell's state nav using the structured target. */
  onOpenHit: (targetType: SearchHitType, targetId: string, fallbackQuery: string) => void;
}

export function GlobalSearch({ syncQuery, onNavigateResults, onOpenHit }: GlobalSearchProps) {
  const t = useTranslate();
  const { recent, remember } = useRecentSearches();

  const [value, setValue] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<DropdownTab>("all");
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownId = useId();

  const query = value.trim();
  const questionLike = isQuestionLike(query);

  // Debounce the input into the search query (Jmix DEBOUNCE_MS).
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(value.trim()), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [value]);

  // syncQueryFromUrl: when the shell navigates to the results page it pushes the query down here; fill
  // the bar and close the dropdown without re-opening it. Skipped when undefined (off the results page).
  useEffect(() => {
    if (syncQuery === undefined) return;
    setValue(syncQuery);
    setOpen(false);
  }, [syncQuery]);

  // Live (non-question, ≥3 chars) literal search — gated with `enabled` on the 3-char floor, on the
  // input not being a question (questions go to AI smart-search, not the literal endpoint), and on the
  // dropdown being open (so landing on the results page, which fills the bar via syncQuery with the
  // dropdown closed, does not fire a redundant dropdown-scope fetch alongside the page's own query).
  const shouldFetch = debounced.length >= MIN_CHARS && !isQuestionLike(debounced);
  const { data, isFetching } = useCustom<SearchResponseDto>({
    url: `search?q=${encodeURIComponent(debounced)}&limitPerType=${DROPDOWN_LIMIT}`,
    method: "get",
    queryOptions: { enabled: shouldFetch && open, keepPreviousData: true },
  });
  const results = shouldFetch ? data?.data : undefined;

  // Reparent-to-<body> + fixed positioning (port of Jmix `positionDropdown`): anchor the portal under
  // the input, clamped to the viewport, recomputed on resize/scroll.
  const reposition = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    const r = input.getBoundingClientRect();
    const w = dropdownRef.current?.offsetWidth ?? 0;
    let left = r.left;
    const maxLeft = window.innerWidth - w - 16;
    if (left > maxLeft) left = Math.max(16, maxLeft);
    setPos({ top: r.bottom + 4, left });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    reposition();
    window.addEventListener("resize", reposition);
    window.addEventListener("scroll", reposition, true);
    return () => {
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
    };
  }, [open, reposition]);

  // Click-outside closes the dropdown (ignore the input wrapper + the body-portal dropdown).
  useEffect(() => {
    if (!open) return;
    const handler = (ev: MouseEvent) => {
      const target = ev.target as Node;
      if (wrapperRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open]);

  const navigate = useCallback(
    (q: string, ai: boolean) => {
      const trimmed = q.trim();
      if (!trimmed) return;
      remember(trimmed);
      setOpen(false);
      onNavigateResults(trimmed, ai);
    },
    [remember, onNavigateResults],
  );

  const openHit = useCallback(
    (hit: SearchHitDto) => {
      setOpen(false);
      setValue("");
      onOpenHit(hit.targetType ?? hit.type, hit.targetId ?? hit.id, query);
    },
    [onOpenHit, query],
  );

  const pickRecent = useCallback((q: string) => {
    setValue(q);
    setOpen(true);
    inputRef.current?.focus();
  }, []);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter") {
      // Read the live DOM value (not the debounced state) — Jmix reads `event.target.value` directly.
      const live = (inputRef.current?.value ?? value).trim();
      if (!live) return;
      navigate(live, isQuestionLike(live));
    }
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setValue(next);
    setOpen(next.trim().length > 0);
  };

  const onFocus = () => {
    // Re-open on focus only at the 3-char floor (Jmix's focus listener re-runs the search only at
    // `>= MIN_CHARS`); a 1–2 char value left in the bar stays closed until the operator types.
    if (value.trim().length >= MIN_CHARS) setOpen(true);
  };

  const groups = orderedGroups(results, tab === "all" ? undefined : tab);
  // De foto bij elke diertreffer, zoals op acc: je herkent het dier voordat je de naam leest. De
  // bytes zitten achter de bearer, dus een <img src> haalt ze niet zelf op — vandaar deze hook, die
  // ze met de token ophaalt en er object-urls van maakt. Zonder foto blijven de initialen staan.
  const animalIds = useMemo(
    () =>
      groups
        .filter((g) => g.display === "animal")
        .flatMap((g) => g.hits.map((hit) => String(hit.targetId ?? hit.id))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [groups.map((g) => g.hits.map((h) => h.id).join(",")).join("|")],
  );
  const photoUrls = useAnimalPhotoUrls(animalIds);
  const total = visibleTotal(results, tab === "all" ? undefined : tab);
  const loading = isFetching || debounced !== query;

  const recentSection = (
    <div className="fawkes-shell-search__recent">
      <div className="fawkes-shell-search__recent-title">
        <ClockIcon width={15} height={15} />
        <span>{t("search.recent.title")}</span>
      </div>
      <div className="fawkes-shell-search__recent-items">
        {recent.map((q) => (
          <button
            key={q}
            type="button"
            className="fawkes-shell-search__recent-item"
            onClick={() => pickRecent(q)}
          >
            {q}
          </button>
        ))}
      </div>
      {/* Placeholder for a future search-history view (Jmix renders it as a dead link too). */}
      <button type="button" className="fawkes-shell-search__recent-all" disabled>
        {t("search.recent.all")}
      </button>
    </div>
  );

  let body: ReactNode;
  if (query.length < MIN_CHARS) {
    body = (
      <>
        <p className="fawkes-shell-search__hint">{t("search.minChars", { args: [MIN_CHARS] })}</p>
        {recentSection}
      </>
    );
  } else if (questionLike) {
    body = (
      <>
        <div className="fawkes-shell-search__ai-block">
          <div className="fawkes-shell-search__ai-header">
            <SparklesIcon className="fawkes-shell-search__ai-icon" width={16} height={16} />
            <span className="fawkes-shell-search__ai-title">
              {t("search.ai.title", { args: [query] })}
            </span>
          </div>
          <span className="fawkes-shell-search__ai-hint">{t("search.ai.hint")}</span>
          <button
            type="button"
            className="fawkes-shell-search__ai-apply"
            onClick={() => navigate(query, true)}
          >
            {t("search.ai.apply")}
            <ArrowRightIcon width={16} height={16} />
          </button>
        </div>
        {recentSection}
      </>
    );
  } else {
    const countLabel = loading
      ? ""
      : total === 0
        ? t("search.count.none")
        : t(total === 1 ? "search.count.one" : "search.count.many", { args: [total] });
    body = (
      <>
        <div className="fawkes-shell-search__tabs" role="tablist">
          {TAB_DEFS.map((td) => (
            <button
              key={td.id}
              type="button"
              role="tab"
              aria-selected={tab === td.id}
              className={
                "fawkes-shell-search__tab" + (tab === td.id ? " fawkes-shell-search__tab--active" : "")
              }
              onClick={() => setTab(td.id)}
            >
              {td.icon}
              <span>{t(td.labelKey)}</span>
            </button>
          ))}
        </div>
        {countLabel ? <div className="fawkes-shell-search__result-count">{countLabel}</div> : null}
        <div className="fawkes-shell-search__two-col">
          <div className="fawkes-shell-search__results">
            {loading ? (
              <p className="fawkes-shell-search__hint">{t("common.loading")}</p>
            ) : groups.length === 0 ? (
              <p className="fawkes-shell-search__empty">{t("search.empty")}</p>
            ) : (
              groups.map((g) => (
                <div key={g.display} className="fawkes-shell-search__group">
                  <div className="fawkes-shell-search__group-header">
                    {groupTypeIcon(g.display)}
                    <span>{t(g.labelKey)}</span>
                  </div>
                  {g.hits.map((hit) => (
                    <SearchHitCard
                      key={`${hit.type}-${hit.id}`}
                      hit={hit}
                      photoUrl={photoUrls[String(hit.targetId ?? hit.id)]}
                      onOpen={openHit}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
          {recentSection}
        </div>
        {!loading && total > 0 ? (
          <div className="fawkes-shell-search__footer">
            <button
              type="button"
              className="fawkes-shell-search__footer-link"
              onClick={() => navigate(query, false)}
            >
              <span className="fawkes-shell-search__footer-link-texts">
                <span className="fawkes-shell-search__footer-link-title">
                  {t("search.footer.title")}
                </span>
                <span className="fawkes-shell-search__footer-link-sub">
                  {t("search.footer.sub", { args: [total] })}
                </span>
              </span>
              <ArrowRightIcon className="fawkes-shell-search__footer-link-arrow" width={18} height={18} />
            </button>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="fawkes-shell-search" ref={wrapperRef}>
      <span className="fawkes-shell-search__icon">
        <SearchIcon width={18} height={18} />
      </span>
      <input
        ref={inputRef}
        className="fawkes-shell-search__input"
        type="search"
        autoComplete="off"
        role="combobox"
        aria-label={t("search.aria.label")}
        aria-expanded={open}
        aria-haspopup="true"
        aria-controls={open ? dropdownId : undefined}
        placeholder={t("search.placeholder")}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
      />
      <button
        type="button"
        className="fawkes-shell-search__smart-pill"
        title={t("search.smartPill.title")}
        onClick={() => inputRef.current?.focus()}
      >
        <SparklesIcon className="fawkes-shell-search__smart-pill-icon" width={15} height={15} />
        <span className="fawkes-shell-search__smart-pill-label">{t("search.smartPill.label")}</span>
      </button>
      {open
        ? createPortal(
            <div
              ref={dropdownRef}
              id={dropdownId}
              role="dialog"
              aria-label={t("search.aria.label")}
              className="fawkes-shell-search__dropdown"
              style={{
                top: pos?.top ?? 0,
                left: pos?.left ?? 0,
                visibility: pos ? "visible" : "hidden",
              }}
            >
              <div className="fawkes-shell-search__dropdown-content">{body}</div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
